import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, Check, X as XIcon, Clock, User } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const TEAL = "#0D9488";
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Coach-side booking hub: manage weekly slots + accept/decline requests.
 *  Renders on the /clients page below CoachAnalyticsCard.
 */
export default function CoachBookingSlotsCard() {
  const [slots, setSlots] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ day_of_week: 1, start_time: "09:00", end_time: "09:30", capacity: 1, label: "" });
  const [saving, setSaving] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/coach/booking-slots").catch(() => ({ data: [] })),
      api.get("/coach/booking-requests").catch(() => ({ data: [] })),
    ]).then(([s, r]) => {
      setSlots(Array.isArray(s.data) ? s.data : []);
      setRequests((Array.isArray(r.data) ? r.data : []).filter((x) => x.is_incoming));
    }).finally(() => setLoading(false));
  };
  useEffect(() => { loadAll(); }, []);

  const addSlot = async () => {
    if (form.start_time >= form.end_time) { toast.error("End time must be after start time."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/coach/booking-slots", form);
      setSlots((prev) => [...prev, data].sort((a, b) => a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time)));
      toast.success("Slot added");
      setForm({ day_of_week: form.day_of_week, start_time: "09:00", end_time: "09:30", capacity: 1, label: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const removeSlot = async (slot) => {
    try {
      await api.delete(`/coach/booking-slots/${slot.id}`);
      setSlots((prev) => prev.filter((x) => x.id !== slot.id));
      toast.success("Slot removed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const decide = async (r, status) => {
    try {
      await api.patch(`/coach/booking-requests/${r.id}`, { status });
      setRequests((prev) => prev.map((x) => x.id === r.id ? { ...x, status } : x));
      toast.success(status === "accepted" ? "Booking accepted" : "Booking declined");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <div className="bg-white border border-slate-200 rounded-xl p-6 h-40 animate-pulse" data-testid="coach-booking-loading" />;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="coach-booking-slots-card">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Booking slots</div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Publish weekly check-in windows</h3>
      <p className="text-sm text-slate-500 mb-5 leading-relaxed">
        Clients can request a specific date on any slot you publish. Delete a slot and pending requests are cancelled automatically.
      </p>

      {/* Existing slots */}
      {slots.length === 0 ? (
        <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-4 text-center mb-4">
          No slots yet — add one below.
        </div>
      ) : (
        <ul className="space-y-2 mb-4" data-testid="coach-booking-slots-list">
          {slots.map((s) => (
            <li key={s.id} className="border border-slate-200 rounded-md p-2.5 flex items-center gap-3" data-testid={`coach-booking-slot-${s.id}`}>
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-teal-50 text-teal-700 font-bold text-[11px] uppercase tracking-widest shrink-0">
                {DAY_LABELS[s.day_of_week]}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">{s.start_time} → {s.end_time}</div>
                <div className="text-[11px] text-slate-500">
                  {s.label ? <>{s.label} · </> : null}
                  <span className="font-mono-num">cap {s.capacity}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeSlot(s)}
                className="w-8 h-8 rounded-md border border-rose-200 hover:bg-rose-50 flex items-center justify-center text-rose-600 shrink-0"
                data-testid={`coach-booking-slot-remove-${s.id}`}
                aria-label="Remove slot"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Add slot */}
      <div className="border-t border-slate-100 pt-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-0.5 block">Day</label>
            <select
              value={form.day_of_week}
              onChange={(e) => setForm((f) => ({ ...f, day_of_week: parseInt(e.target.value, 10) }))}
              className="w-full border border-slate-200 rounded-md h-9 px-2 bg-white text-sm focus:outline-none focus:border-[#0D9488]"
              data-testid="coach-booking-form-day"
            >
              {DAY_LABELS.map((l, i) => <option key={i} value={i}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-0.5 block">Start</label>
            <Input type="time" value={form.start_time}
                   onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                   className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                   data-testid="coach-booking-form-start" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-0.5 block">End</label>
            <Input type="time" value={form.end_time}
                   onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                   className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                   data-testid="coach-booking-form-end" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-0.5 block">Capacity</label>
            <Input type="number" min={1} max={10} value={form.capacity}
                   onChange={(e) => setForm((f) => ({ ...f, capacity: Math.max(1, Math.min(10, parseInt(e.target.value || "1", 10))) }))}
                   className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] font-mono-num"
                   data-testid="coach-booking-form-cap" />
          </div>
          <Button type="button" onClick={addSlot} disabled={saving}
                  className="text-white h-9" style={{ background: TEAL }}
                  data-testid="coach-booking-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        <Input value={form.label}
               onChange={(e) => setForm((f) => ({ ...f, label: e.target.value.slice(0, 60) }))}
               placeholder="Optional label (e.g. Live video session)"
               className="mt-2 bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
               data-testid="coach-booking-form-label" />
      </div>

      {/* Incoming booking requests */}
      {requests.length > 0 && (
        <div className="mt-6 border-t border-slate-100 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Booking requests</span>
            <span className="text-[10px] text-slate-400 tabular-nums">{requests.filter((r) => r.status === "pending").length} pending</span>
          </div>
          <ul className="space-y-2" data-testid="coach-booking-requests-list">
            {requests.map((r) => (
              <li key={r.id} className="border border-slate-200 rounded-md p-3" data-testid={`coach-booking-request-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900">@{r.client_handle || r.client_email}</span>
                  <span className="text-[11px] text-slate-500 font-mono-num">{r.requested_date} · {r.slot?.start_time}–{r.slot?.end_time}</span>
                  <StatusBadge status={r.status} />
                </div>
                {r.note && <div className="text-[12px] text-slate-500 italic mt-1">"{r.note}"</div>}
                {r.status === "pending" && (
                  <div className="flex items-center gap-2 mt-2">
                    <Button type="button" size="sm" onClick={() => decide(r, "accepted")}
                            className="h-8 text-white" style={{ background: TEAL }}
                            data-testid={`coach-booking-accept-${r.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Accept
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => decide(r, "declined")}
                            className="h-8 text-rose-600 hover:bg-rose-50"
                            data-testid={`coach-booking-decline-${r.id}`}>
                      <XIcon className="w-3.5 h-3.5 mr-1" /> Decline
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-50 text-amber-700 border-amber-100",
    accepted: "bg-teal-50 text-teal-700 border-teal-100",
    declined: "bg-slate-100 text-slate-500 border-slate-200",
    cancelled_by_coach: "bg-rose-50 text-rose-600 border-rose-100",
  };
  const c = styles[status] || styles.pending;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold border ${c}`}>
      {status.replace("_", " ")}
    </span>
  );
}
